import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

// Without a boundary, any error thrown while rendering unmounts the whole React
// tree and the page drops to a blank white screen with no explanation. This
// catches those errors and shows an honest message plus the real detail, so a
// crash is recoverable and diagnosable instead of a silent blank.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the underlying cause in the console so a white screen is diagnosable.
    console.error("[01] render error:", error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "var(--bg, #fff)",
          color: "var(--ink, #111)",
          fontFamily: "var(--font-sans, system-ui, sans-serif)",
        }}
      >
        <div style={{ maxWidth: "32rem", textAlign: "left" }}>
          <p style={{ fontFamily: "var(--font-mono, monospace)", opacity: 0.7, margin: "0 0 8px" }}>
            01
          </p>
          <h1 style={{ fontSize: "1.25rem", margin: "0 0 12px", fontWeight: 600 }}>
            Something went wrong on this page
          </h1>
          <p style={{ margin: "0 0 16px", lineHeight: 1.5 }}>
            The page hit an error and stopped. Building a private proof runs in
            your browser and needs a lot of memory, so this can happen on a phone
            or tablet. Reload to try again, or open 01 in a desktop browser.
          </p>
          <pre
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "0.8rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "var(--panel, rgba(0,0,0,0.05))",
              border: "1px solid var(--line, rgba(0,0,0,0.12))",
              borderRadius: "8px",
              padding: "12px",
              margin: "0 0 16px",
              opacity: 0.85,
            }}
          >
            {error.message || String(error)}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              font: "inherit",
              cursor: "pointer",
              padding: "10px 16px",
              borderRadius: "8px",
              border: "1px solid var(--line, rgba(0,0,0,0.2))",
              background: "var(--accent, #1f7a4d)",
              color: "#fff",
            }}
          >
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
